'use client';

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useRouter } from "@/i18n/routing";
import { motion } from "framer-motion";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useState } from "react";
import { FaApple, FaEnvelope, FaFacebook, FaGoogle, FaLock } from "react-icons/fa";

// Define fallback translations
const fallbackTranslations = {
    emailAddress: "Email Address",
    password: "Password",
    forgotPassword: "Forgot password?",
    signIn: "Sign In",
    signingIn: "Signing in...",
    rememberMe: "Remember me",
    dontHaveAccount: "Don't have an account?",
    createAccount: "Create Account",
    orContinueWith: "Or continue with",
    welcomeBack: "Welcome Back",
    enterCredentials: "Sign in to access your personal account",
    invalidCredentials: "Invalid email or password",
    loginSuccess: "You have been logged in",
    somethingWentWrong: "Something went wrong"
};

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const router = useRouter();
    const { addToast } = useToast();

    // Safe access to translations with fallbacks
    let getTranslation = (key: string) => fallbackTranslations[key as keyof typeof fallbackTranslations] || key;

    try {
        // Attempt to use the Auth namespace
        const t = useTranslations('Auth');
        // Override with a function that safely accesses translations
        getTranslation = (key: string) => {
            try {
                return t(key);
            } catch (e) {
                // Fallback to our predefined translations if key doesn't exist
                return fallbackTranslations[key as keyof typeof fallbackTranslations] || key;
            }
        };
    } catch (error) {
        // If Auth namespace doesn't exist, we'll use the fallback function defined above
        console.warn("Auth translation namespace not found, using fallbacks");
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const result = await signIn("credentials", {
                email,
                password,
                redirect: false,
            });

            if (result?.error) {
                addToast(getTranslation("invalidCredentials"), "error");
            } else {
                addToast(getTranslation("loginSuccess"), "success");
                router.push("/");
                router.refresh();
            }
        } catch (error) {
            addToast(getTranslation("somethingWentWrong"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
            {/* Left side - Decorative Panel */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-400 to-emerald-600 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-10 z-0"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-emerald-900/30"></div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="relative z-10 flex flex-col items-center justify-center w-full p-12"
                >
                    <div className="w-24 h-24 mb-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-12 h-12">
                            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-2.625 6c-.54 0-.828.419-.936.634a1.96 1.96 0 00-.189.866c0 .298.059.605.189.866.108.215.395.634.936.634.54 0 .828-.419.936-.634.13-.26.189-.568.189-.866 0-.298-.059-.605-.189-.866-.108-.215-.395-.634-.936-.634zm4.314.634c.108-.215.395-.634.936-.634.54 0 .828.419.936.634.13.26.189.568.189.866 0 .298-.059.605-.189.866-.108.215-.395.634-.936.634-.54 0-.828-.419-.936-.634a1.96 1.96 0 01-.189-.866c0-.298.059-.605.189-.866zm2.023 6.828a.75.75 0 10-1.06-1.06 3.75 3.75 0 01-5.304 0 .75.75 0 00-1.06 1.06 5.25 5.25 0 007.424 0z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <h2 className="text-4xl font-bold text-white mb-4 tracking-tight">Premium Experience</h2>
                    <p className="text-emerald-50 text-center max-w-md leading-relaxed">
                        Discover exceptional restaurants and cafés. Join our community for exclusive access to the finest dining experiences.
                    </p>

                    <div className="mt-16 flex flex-col items-center">
                        <div className="w-28 h-1 bg-emerald-100/30 rounded-full mb-12"></div>
                        <div className="grid grid-cols-3 gap-8">
                            {[1, 2, 3].map((i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: 0.2 * i }}
                                    className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center shadow-xl"
                                >
                                    <div className="w-10 h-10 rounded-full bg-emerald-200/20"></div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Right side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    <div className="text-center mb-10">
                        <motion.h1
                            initial={{ y: -20 }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-3"
                        >
                            {getTranslation("welcomeBack")}
                        </motion.h1>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                            {getTranslation("enterCredentials")}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="space-y-5"
                        >
                            <div className="space-y-1.5">
                                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    {getTranslation("emailAddress")}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <FaEnvelope className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <Input
                                        id="email"
                                        placeholder="name@example.com"
                                        type="email"
                                        autoCapitalize="none"
                                        autoComplete="email"
                                        autoCorrect="off"
                                        disabled={isLoading}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="pl-10 h-12 rounded-xl border-slate-200 dark:border-slate-700 focus:border-emerald-400 focus:ring-emerald-300/30 dark:bg-slate-800 dark:text-white shadow-sm"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    {getTranslation("password")}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <FaLock className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <Input
                                        id="password"
                                        placeholder="Enter your password"
                                        type="password"
                                        autoComplete="current-password"
                                        disabled={isLoading}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="pl-10 h-12 rounded-xl border-slate-200 dark:border-slate-700 focus:border-emerald-400 focus:ring-emerald-300/30 dark:bg-slate-800 dark:text-white shadow-sm"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="remember"
                                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-slate-600 dark:bg-slate-800"
                                    />
                                    <label htmlFor="remember" className="text-sm text-slate-600 dark:text-slate-300">
                                        {getTranslation("rememberMe")}
                                    </label>
                                </div>
                                <Link
                                    href="/forgot-password"
                                    className="text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors font-medium"
                                >
                                    {getTranslation("forgotPassword")}
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <Button
                                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-all duration-200 shadow-md hover:shadow-lg dark:bg-emerald-500 dark:hover:bg-emerald-600"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? getTranslation("signingIn") : getTranslation("signIn")}
                            </Button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="relative my-8"
                        >
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-slate-200 dark:border-slate-700" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-slate-50 dark:bg-slate-900 px-3 text-slate-500 dark:text-slate-400">
                                    {getTranslation("orContinueWith")}
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                            className="grid grid-cols-3 gap-3"
                        >
                            <button
                                type="button"
                                className="flex items-center justify-center h-12 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shadow-sm"
                            >
                                <FaGoogle className="h-5 w-5 text-red-500" />
                            </button>
                            <button
                                type="button"
                                className="flex items-center justify-center h-12 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shadow-sm"
                            >
                                <FaApple className="h-5 w-5 text-slate-800 dark:text-white" />
                            </button>
                            <button
                                type="button"
                                className="flex items-center justify-center h-12 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shadow-sm"
                            >
                                <FaFacebook className="h-5 w-5 text-blue-600" />
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            className="text-center text-sm pt-4 text-slate-600 dark:text-slate-300"
                        >
                            {getTranslation("dontHaveAccount")}{" "}
                            <Link href="/register" className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium">
                                {getTranslation("createAccount")}
                            </Link>
                        </motion.div>
                    </form>
                </motion.div>
            </div>
        </div>
    );
} 